import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiPlus, FiTrash2, FiSearch } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';
import { getAuthToken, getClientContacts } from '../services/api';

const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

function InvoiceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [billToContacts, setBillToContacts] = useState([]);
  const [billToContactId, setBillToContactId] = useState('');
  const [jobs, setJobs] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  
  const [formData, setFormData] = useState({
    client_id: '',
    bill_to_name: '',
    bill_to_company: '',
    bill_to_address: '',
    bill_to_city_state_zip: '',
    bill_to_email: '',
    bill_to_phone: '',
    invoice_date: new Date().toLocaleDateString('en-CA'),
    due_date: '',
    terms: 'Net 30',
    po_number: '',
    notes: '',
    internal_notes: '',
    discount_percent: 0,
    job_ids: [],
    phase_id: '',
    line_items: [{ description: '', quantity: 1, rate: 0, job_id: null, work_order_id: null, time_entry_ids: [] }]
  });

  const [jobPhases, setJobPhases] = useState([]);   // phases of an added phased job (for the phase picker)
  const [showJobSelector, setShowJobSelector] = useState(false);
  const [jobSearchTerm, setJobSearchTerm] = useState('');
  const [workOrders, setWorkOrders] = useState({}); // { jobId: [workOrders] }
  const [importingHours, setImportingHours] = useState(false);
  const [unbilledHours, setUnbilledHours] = useState({}); // { jobId: { hours, count } }

  // Client search state
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load work orders for selected jobs
  const loadWorkOrdersForJob = async (jobId) => {
    if (workOrders[jobId]) return; // Already loaded
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/jobs/${jobId}/work-orders`, config);
      setWorkOrders(prev => ({
        ...prev,
        [jobId]: response.data.work_orders || []
      }));
    } catch (error) {
      console.error('Error loading work orders:', error);
    }
  };

  useEffect(() => {
    loadClients();
    loadJobs();
    loadCompanySettings();
    if (isEditing) {
      loadInvoice();
    }
  }, [id]);

  useEffect(() => {
    // Auto-calculate due date based on terms
    if (formData.invoice_date && formData.terms && !isEditing) {
      const invoiceDate = new Date(formData.invoice_date + 'T12:00:00');
      let days = 30;
      if (formData.terms.includes('Net')) {
        const match = formData.terms.match(/\d+/);
        if (match) days = parseInt(match[0]);
      } else if (formData.terms === 'Due on Receipt') {
        days = 0;
      }
      const dueDate = new Date(invoiceDate);
      dueDate.setDate(dueDate.getDate() + days);
      setFormData(prev => ({ ...prev, due_date: dueDate.toLocaleDateString('en-CA') }));
    }
  }, [formData.invoice_date, formData.terms]);

  // Auto-add job from query parameter (e.g., from Billing page "Create Invoice" button)
  useEffect(() => {
    const preselectedJobId = searchParams.get('job_id');
    if (preselectedJobId && jobs.length > 0 && !isEditing) {
      const jobId = parseInt(preselectedJobId);
      const job = jobs.find(j => j.id === jobId);
      if (job && !formData.job_ids.includes(jobId)) {
        handleAddJob(job);
      }
    }
  }, [jobs, searchParams]);

  // Load work orders and unbilled hours when jobs are selected
  useEffect(() => {
    formData.job_ids.forEach(jobId => {
      loadWorkOrdersForJob(jobId);
      if (!unbilledHours[jobId]) {
        fetchUnbilledHours(jobId);
      }
    });
  }, [formData.job_ids]);

  // Search clients from server with debounce
  useEffect(() => {
    const searchClients = async () => {
      try {
        const config = await getAuthConfig();
        const searchParam = clientSearchTerm.trim() ? `?search=${encodeURIComponent(clientSearchTerm)}&limit=30` : '?limit=30';
        const response = await axios.get(`${API_URL}/clients${searchParam}`, config);
        setFilteredClients(response.data.clients || response.data || []);
      } catch (error) {
        console.error('Error searching clients:', error);
      }
    };

    const timeoutId = setTimeout(searchClients, 200); // Debounce 200ms
    return () => clearTimeout(timeoutId);
  }, [clientSearchTerm]);

  // Set initial client search term when editing
  useEffect(() => {
    const loadSelectedClient = async () => {
      if (formData.client_id && !clientSearchTerm) {
        try {
          const config = await getAuthConfig();
          const response = await axios.get(`${API_URL}/clients/${formData.client_id}`, config);
          const client = response.data;
          if (client) {
            setClientSearchTerm(client.name || '');
            setSelectedClient(client);
          }
        } catch (error) {
          console.error('Error loading selected client:', error);
        }
      }
    };
    loadSelectedClient();
  }, [formData.client_id]);

  // Address the invoice to one person on the client's roster. Contacts carry no address
  // of their own, so company/address/city stay with the client record and only the
  // name/email/phone move.
  const applyBillToContact = (contact) => {
    setBillToContactId(String(contact.id));
    setFormData(prev => ({
      ...prev,
      bill_to_name: contact.name || prev.bill_to_name,
      bill_to_email: contact.email || '',
      bill_to_phone: contact.phone || ''
    }));
  };

  const applyBillToClientRecord = () => {
    setBillToContactId('');
    if (!selectedClient) return;
    const c = selectedClient;
    setFormData(prev => ({
      ...prev,
      bill_to_name: c.name || '',
      bill_to_company: c.company_name || '',
      bill_to_address: c.address || '',
      bill_to_city_state_zip: [c.city, c.state, c.zip_code].filter(Boolean).join(', '),
      bill_to_email: c.email || '',
      bill_to_phone: c.phone || ''
    }));
  };

  // A company can keep a dedicated Accounts Payable person alongside the primary contact.
  // Invoices used to always address the client record's own email, ignoring that roster —
  // so prefer a billing-flagged non-primary contact when the client has one.
  useEffect(() => {
    const loadBillToContacts = async () => {
      if (!formData.client_id) {
        setBillToContacts([]);
        setBillToContactId('');
        return;
      }
      try {
        const res = await getClientContacts(formData.client_id);
        const roster = (res.data || []).filter(c => c.is_active !== false);
        setBillToContacts(roster);

        // Only auto-address a NEW invoice. An existing one keeps what it was saved with,
        // or reopening it to edit would silently re-address it.
        if (isEditing) return;
        const ap = roster.find(c => c.receives_billing && !c.is_primary && c.email);
        if (ap) applyBillToContact(ap);
      } catch (e) {
        setBillToContacts([]);
      }
    };
    loadBillToContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.client_id, isEditing]);

  const handleClientSelect = (client) => {
    // Build city, state, zip string
    const cityStateZip = [
      client.city,
      client.state,
      client.zip_code
    ].filter(Boolean).join(', ');

    setFormData(prev => ({
      ...prev,
      client_id: client.id.toString(),
      bill_to_name: client.name || '',
      bill_to_company: client.company_name || '',
      bill_to_address: client.address || '',
      bill_to_city_state_zip: cityStateZip,
      bill_to_email: client.email || '',
      bill_to_phone: client.phone || ''
    }));
    setSelectedClient(client);
    setBillToContactId(''); // the old client's roster contact no longer applies
    setClientSearchTerm(client.name || '');
    setShowClientDropdown(false);
  };

  const loadClients = async () => {
    // No longer loading all clients upfront - using server-side search instead
  };

  const loadJobs = async () => {
    try {
      const config = await getAuthConfig();
      // Picker list only: lite omits the relationship walk that made this ~10 MB.
      const response = await axios.get(`${API_URL}/jobs?fields=lite`, config);
      setJobs(response.data.jobs || response.data || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  };

  const loadCompanySettings = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/company-settings`, config);
      setCompanySettings(response.data);
      if (!isEditing && response.data.default_notes) {
        setFormData(prev => ({ ...prev, notes: response.data.default_notes }));
      }
      if (!isEditing && response.data.default_terms) {
        setFormData(prev => ({ ...prev, terms: response.data.default_terms }));
      }
    } catch (error) {
      console.error('Error loading company settings:', error);
    }
  };

  const loadInvoice = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/invoices/${id}`, config);
      const invoice = response.data;
      setFormData({
        client_id: invoice.client_id || '',
        bill_to_name: invoice.bill_to_name || '',
        bill_to_company: invoice.bill_to_company || '',
        bill_to_address: invoice.bill_to_address || '',
        bill_to_city_state_zip: invoice.bill_to_city_state_zip || '',
        bill_to_email: invoice.bill_to_email || '',
        bill_to_phone: invoice.bill_to_phone || '',
        invoice_date: invoice.invoice_date || '',
        due_date: invoice.due_date || '',
        terms: invoice.terms || 'Net 30',
        po_number: invoice.po_number || '',
        notes: invoice.notes || '',
        internal_notes: invoice.internal_notes || '',
        discount_percent: invoice.discount_percent || 0,
        job_ids: invoice.jobs?.map(j => j.id) || [],
        line_items: invoice.line_items?.length > 0
          ? invoice.line_items.map(item => ({
              description: item.description,
              quantity: item.quantity,
              rate: item.rate,
              job_id: item.job_id,
              work_order_id: item.work_order_id,
              time_entry_ids: [] // Existing line items already have entries linked via backend
            }))
          : [{ description: '', quantity: 1, rate: 0, job_id: null, work_order_id: null, time_entry_ids: [] }]
      });
      setLoading(false);
    } catch (error) {
      console.error('Error loading invoice:', error);
      setLoading(false);
    }
  };

  // Clients are searched server-side, so there is no local list to look the id up in —
  // fetch the record. Without this, picking a phase only swapped the hidden client_id
  // and left the previous client's name/address in Bill To.
  const latestClientRequest = useRef(null);
  const handleClientChange = async (clientId) => {
    latestClientRequest.current = String(clientId);
    try {
      const res = await axios.get(`${API_URL}/clients/${clientId}`, await getAuthConfig());
      if (latestClientRequest.current !== String(clientId)) return; // a newer pick won
      if (res.data) handleClientSelect(res.data);
    } catch (error) {
      if (latestClientRequest.current !== String(clientId)) return;
      setFormData(prev => ({ ...prev, client_id: String(clientId) }));
    }
  };

  const handleAddLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, rate: 0, job_id: null, work_order_id: null, time_entry_ids: [] }]
    }));
  };

  const handleRemoveLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const fetchUnbilledHours = async (jobId) => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/time-entries?type=job&job_id=${jobId}&uninvoiced_only=true`, config);
      const entries = response.data.time_entries || [];
      // Deduplicate crew entries - only count hours once per clock_in_group
      const seenGroups = new Set();
      let totalHours = 0;
      let uniqueCount = 0;
      entries.forEach(e => {
        if (e.clock_in_group) {
          if (seenGroups.has(e.clock_in_group)) return;
          seenGroups.add(e.clock_in_group);
        }
        totalHours += e.total_hours || 0;
        uniqueCount++;
      });
      setUnbilledHours(prev => ({
        ...prev,
        [jobId]: { hours: Math.round(totalHours * 100) / 100, count: uniqueCount }
      }));
    } catch (error) {
      // Silently fail
    }
  };

  const handleAddJob = (job) => {
    if (!formData.job_ids.includes(job.id)) {
      setFormData(prev => ({
        ...prev,
        job_ids: [...prev.job_ids, job.id],
        // Optionally add a line item for the job
        line_items: [
          ...prev.line_items.filter(item => item.description), // Remove empty items
          {
            description: job.description_of_work || `Survey services for ${job.property_address || job.job_number}`,
            quantity: 1,
            rate: 0,
            job_id: job.id,
            work_order_id: null
          }
        ]
      }));
      
      // Load work orders for this job
      loadWorkOrdersForJob(job.id);

      // Fetch unbilled hours for this job
      fetchUnbilledHours(job.id);

      // If no client selected, use job's client
      if (!formData.client_id && job.client_id) {
        handleClientChange(job.client_id.toString());
      }

      // Phased job (Design/Construction): load its phases so the user can pick which
      // client to bill. Default to the first phase.
      if (job.has_phases) {
        (async () => {
          try {
            const res = await axios.get(`${API_URL}/jobs/${job.id}/phases`, await getAuthConfig());
            const phases = res.data?.phases || [];
            setJobPhases(phases);
            if (phases.length) selectPhase(phases[0], phases);
          } catch (e) { /* leave as job client */ }
        })();
      } else {
        setJobPhases([]);
        setFormData(prev => ({ ...prev, phase_id: '' }));
      }
    }
    setShowJobSelector(false);
    setJobSearchTerm('');
  };

  // Bill a specific phase: set phase_id and fill bill-to from the phase's client.
  const selectPhase = (phase, phasesList) => {
    const list = phasesList || jobPhases;
    const p = typeof phase === 'object' ? phase : list.find(x => String(x.id) === String(phase));
    if (!p) { setFormData(prev => ({ ...prev, phase_id: '' })); return; }
    setFormData(prev => ({ ...prev, phase_id: p.id }));
    if (p.client_id) handleClientChange(p.client_id.toString());
  };

  const handleRemoveJob = (jobId) => {
    setFormData(prev => ({
      ...prev,
      job_ids: prev.job_ids.filter(id => id !== jobId)
    }));
  };

  // Import hours from job time entries as line items
  const handleImportHoursFromJob = async (jobId) => {
    if (!jobId) return;

    setImportingHours(true);
    try {
      const config = await getAuthConfig();
      // Only fetch uninvoiced entries to prevent re-importing already-invoiced hours.
      // On a phased invoice, pull ONLY the selected phase's time so each client is
      // billed only for their phase's hours.
      const phaseParam = formData.phase_id ? `&phase_id=${formData.phase_id}` : '';
      const response = await axios.get(`${API_URL}/time-entries?type=job&job_id=${jobId}&uninvoiced_only=true${phaseParam}`, config);
      const entries = response.data.time_entries || [];

      if (entries.length === 0) {
        alert(formData.phase_id
          ? 'No unbilled time is tagged to this phase yet. Tag time to the phase on the job’s Time Entries tab first.'
          : 'No time entries found for this job.');
        setImportingHours(false);
        return;
      }
      
      // Deduplicate crew entries - only count each work session once
      // Group by: clock_in_group OR (same clock_in + clock_out times)
      const processedKeys = new Set();
      const uniqueEntries = [];
      
      entries.forEach(entry => {
        let dedupeKey;
        
        if (entry.clock_in_group) {
          // Crew entry - use group ID
          dedupeKey = `group:${entry.clock_in_group}`;
        } else {
          // Solo entry or old entry without group - dedupe by exact times
          // This prevents double-counting when multiple people have identical times
          dedupeKey = `times:${entry.clock_in}|${entry.clock_out}|${entry.work_type || ''}`;
        }
        
        if (!processedKeys.has(dedupeKey)) {
          processedKeys.add(dedupeKey);
          uniqueEntries.push(entry);
        }
      });
      
      // Group unique entries by date AND work type. Travel and field work on the same
      // day bill at different rates, so they must land on separate lines.
      const entriesByDate = {};
      uniqueEntries.forEach(entry => {
        if (!entry.clock_in) return;
        
        // Parse date and convert to local date key
        let clockIn = new Date(entry.clock_in);
        if (!entry.clock_in.includes('Z') && !entry.clock_in.includes('+')) {
          clockIn = new Date(entry.clock_in + 'Z');
        }
        const dateKey = clockIn.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
        const groupKey = `${dateKey}|${entry.work_type || ''}`;

        if (!entriesByDate[groupKey]) {
          entriesByDate[groupKey] = {
            date: dateKey,
            firstClockIn: clockIn,
            entries: [],
            descriptions: new Set(),
            workTypes: new Set()
          };
        }
        const group = entriesByDate[groupKey];
        if (clockIn < group.firstClockIn) group.firstClockIn = clockIn;

        group.entries.push(entry);
        if (entry.work_type) {
          group.workTypes.add(entry.work_type);
        }
        if (entry.notes) {
          group.descriptions.add(entry.notes);
        }
      });
      
      // Create line items for each date
      // Sum actual hours from each unique entry/group (not time span)
      const newLineItems = Object.values(entriesByDate).map(dayGroup => {
        // Sum hours from each entry (total_hours already accounts for breaks/pauses)
        let totalHours = 0;
        dayGroup.entries.forEach(entry => {
          if (entry.total_hours) {
            totalHours += entry.total_hours;
          } else if (entry.clock_in && entry.clock_out) {
            // Fallback calculation if total_hours not available
            let clockIn = new Date(entry.clock_in);
            let clockOut = new Date(entry.clock_out);
            if (!entry.clock_in.includes('Z') && !entry.clock_in.includes('+')) {
              clockIn = new Date(entry.clock_in + 'Z');
            }
            if (!entry.clock_out.includes('Z') && !entry.clock_out.includes('+')) {
              clockOut = new Date(entry.clock_out + 'Z');
            }
            totalHours += (clockOut - clockIn) / (1000 * 60 * 60);
          }
        });
        totalHours = Math.round(totalHours * 100) / 100; // Round to 2 decimal places
        
        // Build description
        const workTypes = Array.from(dayGroup.workTypes);
        const descriptions = Array.from(dayGroup.descriptions).filter(d => d);
        let description = dayGroup.date;
        if (workTypes.length > 0) {
          description += ' - ' + workTypes.join(', ');
        }
        if (descriptions.length > 0) {
          description += ': ' + descriptions.join('; ');
        }
        
        // Collect all time entry IDs for this date group
        const timeEntryIds = dayGroup.entries.map(entry => entry.id);

        return {
          description: description,
          quantity: totalHours,
          rate: 0, // User sets the hourly rate
          job_id: jobId,
          work_order_id: null,
          time_entry_ids: timeEntryIds,
          _sortAt: dayGroup.firstClockIn
        };
      });
      
      // Chronological: by day, and within a day in the order the work happened
      newLineItems.sort((a, b) => a._sortAt - b._sortAt);
      newLineItems.forEach(item => { delete item._sortAt; });
      
      // Add to existing line items (remove empty ones first)
      setFormData(prev => ({
        ...prev,
        line_items: [
          ...prev.line_items.filter(item => item.description),
          ...newLineItems
        ]
      }));
      
      alert(`Imported ${newLineItems.length} line item(s) from time entries.`);
      // Clear unbilled hours badge for this job
      setUnbilledHours(prev => { const next = { ...prev }; delete next[jobId]; return next; });
    } catch (error) {
      console.error('Error importing hours:', error);
      alert('Failed to import hours from job.');
    }
    setImportingHours(false);
  };

  const calculateSubtotal = () => {
    return formData.line_items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  };

  const calculateDiscount = () => {
    return calculateSubtotal() * (formData.discount_percent / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const config = await getAuthConfig();
      const payload = {
        ...formData,
        line_items: formData.line_items.filter(item => item.description)
      };
      
      if (isEditing) {
        await axios.put(`${API_URL}/invoices/${id}`, payload, config);
      } else {
        await axios.post(`${API_URL}/invoices`, payload, config);
      }
      
      navigate('/billing?tab=invoices');
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert('Failed to save invoice');
      setSaving(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    if (!jobSearchTerm) return true;
    const search = jobSearchTerm.toLowerCase();
    return (
      job.job_number?.toLowerCase().includes(search) ||
      job.property_address?.toLowerCase().includes(search) ||
      job.client_name?.toLowerCase().includes(search)
    );
  }).filter(job => !formData.job_ids.includes(job.id));

  const selectedJobs = jobs.filter(job => formData.job_ids.includes(job.id));

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: isMobile ? '12px' : '20px', maxWidth: '1000px', margin: '0 auto', overflowX: 'hidden' }}>
      <style>{`
        .invoice-form input, .invoice-form select, .invoice-form textarea {
          box-sizing: border-box;
        }
      `}</style>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/billing?tab=invoices')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            marginBottom: '16px',
            padding: '0'
          }}
        >
          <FiArrowLeft /> Back to Billing
        </button>
        <h1 style={{ margin: 0, fontSize: '28px', color: '#1f2937' }}>
          {isEditing ? 'Edit Invoice' : 'New Invoice'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="invoice-form">
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '16px' : '24px' }}>

          {/* Left Column - Bill To */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: isMobile ? '16px' : '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ margin: '0 0 20px', fontSize: '18px', color: '#374151' }}>Bill To</h2>

            {jobPhases.length > 0 && (
              <div style={{ marginBottom: '16px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px', padding: '12px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px', color: '#3730a3' }}>
                  This job has phases — which one is this invoice for?
                </label>
                <select
                  value={formData.phase_id || ''}
                  onChange={(e) => selectPhase(e.target.value)}
                  style={{ width: '100%', padding: '9px', borderRadius: '6px', border: '1px solid #c7d2fe', fontSize: '14px', background: 'white' }}
                >
                  <option value="">— Choose a phase —</option>
                  {jobPhases.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {p.client_name || 'no client set'}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: '12px', color: '#4338ca', marginTop: '6px' }}>
                  The Bill To below fills in from the phase's client. You can still edit it.
                </div>
              </div>
            )}

            <div style={{ marginBottom: '16px', position: 'relative' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Client
              </label>
              <div style={{ position: 'relative' }}>
                <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  type="text"
                  value={clientSearchTerm}
                  onChange={(e) => {
                    setClientSearchTerm(e.target.value);
                    setShowClientDropdown(true);
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                  placeholder="Search clients..."
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 36px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
                {formData.client_id && (
                  <button
                    type="button"
                    onClick={() => {
                      setClientSearchTerm('');
                      setFormData(prev => ({
                        ...prev,
                        client_id: '',
                        bill_to_name: '',
                        bill_to_company: '',
                        bill_to_address: '',
                        bill_to_city_state_zip: '',
                        bill_to_email: '',
                        bill_to_phone: ''
                      }));
                    }}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: '#f3f4f6',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      color: '#6b7280'
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
              {showClientDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  maxHeight: '250px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  marginTop: '4px'
                }}>
                  {filteredClients.length === 0 ? (
                    <div style={{ padding: '12px', color: '#6b7280', textAlign: 'center' }}>
                      No clients found
                    </div>
                  ) : (
                    filteredClients.map(client => (
                      <div
                        key={client.id}
                        onClick={() => handleClientSelect(client)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f3f4f6',
                          backgroundColor: formData.client_id === client.id.toString() ? '#eff6ff' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = formData.client_id === client.id.toString() ? '#eff6ff' : 'transparent'}
                      >
                        <div style={{ fontWeight: '500', fontSize: '14px' }}>{client.name}</div>
                        {client.company_name && (
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{client.company_name}</div>
                        )}
                        {client.email && (
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>{client.email}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Name
              </label>
              {billToContacts.length > 1 && (
                <div style={{ marginBottom: '10px' }}>
                  <select
                    value={billToContactId}
                    onChange={(e) => {
                      const picked = billToContacts.find(c => String(c.id) === e.target.value);
                      if (picked) applyBillToContact(picked);
                      else applyBillToClientRecord();
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: '#fff'
                    }}
                  >
                    <option value="">{selectedClient?.name || 'Client record'}</option>
                    {billToContacts.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.title ? ' — ' + c.title : ''}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Who the invoice is addressed to. Everyone flagged Billing on this client
                    still receives the emailed copy.
                  </div>
                </div>
              )}
              <input
                type="text"
                value={formData.bill_to_name}
                onChange={(e) => setFormData(prev => ({ ...prev, bill_to_name: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Company
              </label>
              <input
                type="text"
                value={formData.bill_to_company}
                onChange={(e) => setFormData(prev => ({ ...prev, bill_to_company: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Address
              </label>
              <input
                type="text"
                value={formData.bill_to_address}
                onChange={(e) => setFormData(prev => ({ ...prev, bill_to_address: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                City, State, ZIP
              </label>
              <input
                type="text"
                value={formData.bill_to_city_state_zip}
                onChange={(e) => setFormData(prev => ({ ...prev, bill_to_city_state_zip: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={formData.bill_to_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, bill_to_email: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.bill_to_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, bill_to_phone: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Invoice Details */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: isMobile ? '16px' : '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ margin: '0 0 20px', fontSize: '18px', color: '#374151' }}>Invoice Details</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Invoice Date
                </label>
                <input
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Terms
                </label>
                <select
                  value={formData.terms}
                  onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="Due on Receipt">Due on Receipt</option>
                  <option value="Net 15">Net 15</option>
                  <option value="Net 30">Net 30</option>
                  <option value="Net 45">Net 45</option>
                  <option value="Net 60">Net 60</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  P.O. Number
                </label>
                <input
                  type="text"
                  value={formData.po_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, po_number: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  placeholder="Optional"
                />
              </div>
            </div>

            {/* Jobs Section */}
            <div style={{ marginTop: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <label style={{ fontWeight: '500', fontSize: '14px' }}>Related Jobs</label>
                <button
                  type="button"
                  onClick={() => setShowJobSelector(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  <FiPlus size={14} /> Add Job
                </button>
              </div>
              
              {selectedJobs.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedJobs.map(job => (
                    <div
                      key={job.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: '#dbeafe',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    >
                      <span style={{ fontWeight: '500' }}>{job.job_number}</span>
                      {unbilledHours[job.id] && unbilledHours[job.id].hours > 0 && (
                        <span style={{
                          backgroundColor: '#f59e0b',
                          color: '#fff',
                          padding: '1px 6px',
                          borderRadius: '8px',
                          fontSize: '10px',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}>
                          {unbilledHours[job.id].hours}h unbilled
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleImportHoursFromJob(job.id)}
                        disabled={importingHours}
                        title="Import time entries as line items"
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '2px 6px',
                          cursor: importingHours ? 'wait' : 'pointer',
                          color: '#2563eb',
                          fontSize: '11px',
                          fontWeight: '500',
                          borderRadius: '4px',
                          backgroundColor: '#bfdbfe'
                        }}
                      >
                        {importingHours ? '...' : 'Import Hours'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveJob(job.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '0',
                          cursor: 'pointer',
                          color: '#6b7280'
                        }}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: '#f9fafb', 
                  borderRadius: '8px',
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '14px'
                }}>
                  No jobs linked to this invoice
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginTop: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: '0 0 20px', fontSize: '18px', color: '#374151' }}>Line Items</h2>
          
          {isMobile ? (
            /* Mobile: card-based line items */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {formData.line_items.map((item, index) => (
                <div key={index} style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                    placeholder="Description"
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <select
                    value={item.work_order_id || ''}
                    onChange={(e) => handleLineItemChange(index, 'work_order_id', e.target.value ? parseInt(e.target.value) : null)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      backgroundColor: 'white',
                      marginBottom: '8px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">No Work Order</option>
                    {formData.job_ids.flatMap(jobId =>
                      (workOrders[jobId] || []).map(wo => (
                        <option key={wo.id} value={wo.id}>
                          {jobs.find(j => j.id === jobId)?.job_number} - {wo.order_number}: {wo.description}
                          {wo.po_number ? ` (PO: ${wo.po_number})` : ''}
                        </option>
                      ))
                    )}
                  </select>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'center' }}>
                    <div>
                      <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Qty</label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          textAlign: 'center',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Rate</label>
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => handleLineItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          textAlign: 'right',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    {formData.line_items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveLineItem(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc2626',
                          cursor: 'pointer',
                          padding: '4px',
                          marginTop: '16px'
                        }}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', marginTop: '8px', fontWeight: '600', fontSize: '15px' }}>
                    {formatCurrency(item.quantity * item.rate)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Desktop: table layout */
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '10px', textAlign: 'left', width: '35%' }}>Description</th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '20%' }}>Work Order</th>
                  <th style={{ padding: '10px', textAlign: 'center', width: '10%' }}>Qty</th>
                  <th style={{ padding: '10px', textAlign: 'right', width: '12%' }}>Rate</th>
                  <th style={{ padding: '10px', textAlign: 'right', width: '12%' }}>Amount</th>
                  <th style={{ padding: '10px', width: '6%' }}></th>
                </tr>
              </thead>
              <tbody>
                {formData.line_items.map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '10px' }}>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                        placeholder="Description of service or product"
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </td>
                    <td style={{ padding: '10px' }}>
                      <select
                        value={item.work_order_id || ''}
                        onChange={(e) => handleLineItemChange(index, 'work_order_id', e.target.value ? parseInt(e.target.value) : null)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '13px',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">No Work Order</option>
                        {formData.job_ids.flatMap(jobId =>
                          (workOrders[jobId] || []).map(wo => (
                            <option key={wo.id} value={wo.id}>
                              {jobs.find(j => j.id === jobId)?.job_number} - {wo.order_number}: {wo.description}
                              {wo.po_number ? ` (PO: ${wo.po_number})` : ''}
                            </option>
                          ))
                        )}
                      </select>
                    </td>
                    <td style={{ padding: '10px' }}>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}
                      />
                    </td>
                    <td style={{ padding: '10px' }}>
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => handleLineItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          textAlign: 'right'
                        }}
                      />
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: '500' }}>
                      {formatCurrency(item.quantity * item.rate)}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      {formData.line_items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveLineItem(index)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#dc2626',
                            cursor: 'pointer',
                            padding: '4px'
                          }}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <button
            type="button"
            onClick={handleAddLineItem}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: '12px',
              padding: '8px 16px',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <FiPlus size={14} /> Add Line Item
          </button>

          {/* Totals */}
          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: isMobile ? '100%' : '300px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span>Subtotal:</span>
                <span style={{ fontWeight: '500' }}>{formatCurrency(calculateSubtotal())}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>Discount:</span>
                  <input
                    type="number"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount_percent: parseFloat(e.target.value) || 0 }))}
                    min="0"
                    max="100"
                    step="0.5"
                    style={{
                      width: '60px',
                      padding: '4px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      textAlign: 'right'
                    }}
                  />
                  <span>%</span>
                </div>
                <span style={{ color: '#dc2626' }}>-{formatCurrency(calculateDiscount())}</span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                padding: '12px 0',
                borderTop: '2px solid #e5e7eb',
                marginTop: '8px',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                <span>Total:</span>
                <span>{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginTop: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '16px' : '24px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Notes (visible on invoice)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows="4"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                placeholder="Thank you for your business!"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Internal Notes (not visible to client)
              </label>
              <textarea
                value={formData.internal_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, internal_notes: e.target.value }))}
                rows="4"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                placeholder="Private notes about this invoice..."
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '24px',
          paddingBottom: '40px'
        }}>
          <button
            type="button"
            onClick={() => navigate('/billing?tab=invoices')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '15px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '500',
              cursor: 'pointer',
              opacity: saving ? 0.7 : 1
            }}
          >
            <FiSave /> {saving ? 'Saving...' : (isEditing ? 'Update Invoice' : 'Create Invoice')}
          </button>
        </div>
      </form>

      {/* Job Selector Modal */}
      {showJobSelector && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 style={{ margin: 0 }}>Select a Job</h3>
              <button
                onClick={() => {
                  setShowJobSelector(false);
                  setJobSearchTerm('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ position: 'relative', marginBottom: '16px' }}>
              <FiSearch style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }} />
              <input
                type="text"
                placeholder="Search jobs..."
                value={jobSearchTerm}
                onChange={(e) => setJobSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {filteredJobs.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                  No jobs found
                </div>
              ) : (
                filteredJobs.slice(0, 50).map(job => (
                  <div
                    key={job.id}
                    onClick={() => handleAddJob(job)}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <div style={{ fontWeight: '500', marginBottom: '4px' }}>{job.job_number}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {job.client_name} • {job.property_address}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoiceForm;